import { useQueryClient } from '@tanstack/react-query';
import { useEffect, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { ApiError } from '../lib/api';
import { ME_QUERY_KEY, useMe } from '../lib/auth';
import { subscribeToUnauthorized } from '../lib/events';

/**
 * Gate for every app route. A 401 anywhere — the session probe itself, or any later request whose
 * session expired — funnels back through `GET /api/me` and ends at `/login`. Loading/error states
 * read as broadcast telemetry, not a spinner.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const location = useLocation();
  const { data: me, isPending, error } = useMe();

  useEffect(() => {
    // A 401 from any mutation means the session is gone; re-probe rather than trusting the cache.
    return subscribeToUnauthorized(() => {
      void queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
    });
  }, [queryClient]);

  if (isPending) {
    return (
      <div className="flex min-h-full items-center justify-center p-6">
        <p className="telemetry text-text-faint">Checking session…</p>
      </div>
    );
  }

  if (error) {
    if (error instanceof ApiError && error.status === 401) {
      return <Navigate to="/login" replace state={{ from: location.pathname }} />;
    }
    // Anything else (API down, 5xx) is not an auth problem — saying "log in" would be a lie.
    return (
      <div className="flex min-h-full items-center justify-center p-6">
        <p role="alert" className="telemetry text-signal">
          Could not reach the Strylander API. Please retry in a moment.
        </p>
      </div>
    );
  }

  return me ? <>{children}</> : <Navigate to="/login" replace />;
}
